import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { FiX, FiCheck, FiAlertCircle, FiArrowLeft, FiRefreshCw } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL, getTeamImageUrl } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { useChampionships } from '../utilities/championships';
import { champFields } from '../utilities/tournamentsApi';
import { extractLeagueMatchDefaults } from '../utilities/matchesApi';
import { kickoffFieldsForApi, resolveMatchTimezone } from '../utilities/matchKickoff';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import moment from 'moment';
import '../styles/createMatchModal.css';
import '../styles/stagesManagement.css';

/**
 * GenerateTourModal — генерация матчей для стадии (тура)
 * 
 * - Проверяет дубликаты (не создаёт матчи, которые уже есть)
 * - Чередует дом/гости для команд между турами
 * - Поддерживает группы (league_type === 2): команды из разных групп не пересекаются
 * - Показывает превью сгенерированных матчей перед подтверждением
 */
function GenerateTourModal({ isOpen, onClose, league, season, stage, previousStage, onSuccess }) {
  const dispatch = useDispatch();
  const { champId, selected: championship } = useChampionships();

  // Шаг: 'settings' или 'preview'
  const [step, setStep] = useState('settings');

  // Настройки матчей
  const [matchDate, setMatchDate] = useState('');
  const [matchTime, setMatchTime] = useState('');
  const [selectedStadium, setSelectedStadium] = useState('');
  const [selectedJudge, setSelectedJudge] = useState('');
  const [halfTime, setHalfTime] = useState('');

  // Справочники
  const [stadiums, setStadiums] = useState([]);
  const [judges, setJudges] = useState([]);

  // Данные для генерации
  const [teams, setTeams] = useState([]);
  const [groups, setGroups] = useState([]);
  const [teamGroupMap, setTeamGroupMap] = useState({}); // { teamId: groupName }
  const [existingMatches, setExistingMatches] = useState([]);
  const [allSeasonMatches, setAllSeasonMatches] = useState([]);

  // Превью
  const [generatedMatches, setGeneratedMatches] = useState([]);
  const [disabledMatches, setDisabledMatches] = useState(new Set());
  const [availableTours, setAvailableTours] = useState([]); // Все доступные туры из таблицы Бергера
  const [currentTourIndex, setCurrentTourIndex] = useState(0); // Текущий выбранный тур

  // UI
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (isOpen) {
      resetForm();
      loadInitialData();
    }
  }, [isOpen]);

  const loadInitialData = async () => {
    // Загружаем справочники (стадионы, судьи) — они нужны для формы
    // Остальное (команды, матчи, группы) загружаем параллельно,
    // но не блокируем UI — генерация сама перезагрузит при необходимости
    try {
      await Promise.all([
        loadStadiums(),
        loadJudges()
      ]);
    } catch (error) {
      console.error('Error loading stadiums/judges:', error);
    }
    // Некритичные данные — загружаем в фоне, не блокируя
    Promise.all([
      loadTeams(),
      loadExistingMatches(),
      loadAllSeasonMatches(),
      loadGroups()
    ]).catch(err => console.error('Error loading background data:', err));
  };

  const getAuthHeaders = async () => {
    const isTokenExpired = checkIfTokenExpired();
    if (isTokenExpired) {
      await dispatch(refreshTokens());
    }
    const tokens = getTokensFromStore();
    return {
      Authorization: "Bearer " + tokens.access_token,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  };

  const loadStadiums = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + '/stadiums/getAllStadiums',
        {},
        { headers }
      );
      const stadiumsData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setStadiums(stadiumsData.map((s) => ({ ...s, name: s.name, value: s.id })));

      const defaults = extractLeagueMatchDefaults(league);
      if (defaults.stadiumId) setSelectedStadium(defaults.stadiumId);
      if (defaults.halfTime) setHalfTime(defaults.halfTime);
    } catch (error) {
      console.error('Error loading stadiums:', error);
    }
  };

  const loadJudges = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + '/judges/getAllReferees',
        {},
        { headers }
      );
      const judgesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setJudges(judgesData.map(j => ({
        name: `${j.name || ''} ${j.family || ''}`.trim(),
        value: j.id
      })));

      const defaults = extractLeagueMatchDefaults(league);
      if (defaults.refereeId) setSelectedJudge(defaults.refereeId);
    } catch (error) {
      console.error('Error loading judges:', error);
    }
  };

  const loadTeams = async () => {
    if (!league || !season) return;
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + `/leagues/${league.value}/teams`,
        { season_id: season.value, ...champFields(champId) },
        { headers }
      );
      const teamsData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setTeams(teamsData);
    } catch (error) {
      console.error('Error loading teams:', error);
      setTeams([]);
    }
  };

  const loadGroups = async () => {
    if (!league || !season) return;
    if (parseInt(league.league_type) !== 2) {
      setGroups([]);
      setTeamGroupMap({});
      return;
    }
    try {
      const headers = await getAuthHeaders();
      const map = {};

      // Используем v2/tournamentTable — каждая команда имеет поле group_name
      try {
        const tableResp = await axios.post(
          BASE_URL.url + `/leagues/${league.value}/v2/tournamentTable`,
          { season_id: parseInt(season.value) },
          { headers, timeout: 10000 }
        );
        const tableData = tableResp.data?.table_data || tableResp.data;
        if (Array.isArray(tableData)) {
          tableData.forEach(team => {
            const tId = team.team_id || team.id;
            const gName = team.group_name;
            if (tId && gName) {
              map[tId] = gName;
            }
          });
        }
      } catch (err) {
        // v2/tournamentTable недоступен
      }

      setTeamGroupMap(map);
    } catch (error) {
      console.error('Error loading groups:', error);
      setGroups([]);
      setTeamGroupMap({});
    }
  };

  const loadExistingMatches = async () => {
    if (!league || !season || !stage) return;
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + `/leagues/${league.value}/matches`,
        { season_id: season.value, stage_id: stage.id },
        { headers }
      );
      const matchesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setExistingMatches(matchesData.filter(m =>
        m.stage_id === stage.id || m.tour_id === stage.id
      ));
    } catch (error) {
      console.error('Error loading existing matches:', error);
      setExistingMatches([]);
    }
  };

  const loadAllSeasonMatches = async () => {
    if (!league || !season) return;
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + `/leagues/${league.value}/matches`,
        { season_id: season.value },
        { headers }
      );
      const matchesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setAllSeasonMatches(matchesData);
    } catch (error) {
      console.error('Error loading season matches:', error);
      setAllSeasonMatches([]);
    }
  };

  /**
   * Определяет предпочтительную сторону (home/away) для команды
   * на основе предыдущих матчей сезона. Для чередования дом/гости.
   */
  const getPreferredSide = useCallback((teamId) => {


    const teamMatches = allSeasonMatches.filter(m =>
      (m.stage_id !== stage?.id && m.tour_id !== stage?.id) &&
      (m.home_team_id === teamId || m.guest_team_id === teamId)
    );
    if (teamMatches.length === 0) return null;

    // Сортируем по stage_id/tour_id от нового к старому
    const sorted = [...teamMatches].sort((a, b) => {
      const sA = a.stage_id || a.tour_id || 0;
      const sB = b.stage_id || b.tour_id || 0;
      return sB - sA;
    });

    // Основной принцип: если в последнем матче был дома — теперь в гости, и наоборот
    const lastMatch = sorted[0];
    if (lastMatch) {
      return lastMatch.home_team_id === teamId ? 'away' : 'home';
    }

    let homeCount = 0, awayCount = 0;
    teamMatches.forEach(m => {
      if (m.home_team_id === teamId) homeCount++;
      else awayCount++;
    });
    if (homeCount > awayCount) return 'away';
    if (awayCount > homeCount) return 'home';
    return null;
  }, [allSeasonMatches, stage]);

  const applyHomeAway = useCallback((pair) => {
    let circleCount = 0;
    if(allSeasonMatches && allSeasonMatches.length && allSeasonMatches.length > 0 && teams && teams.length > 0){
        const matches_in_circle = teams.length * (teams.length - 1) / 2;
        circleCount = Math.trunc(allSeasonMatches.length / matches_in_circle) + 1;
    }
    let pref1 = getPreferredSide(pair.home_team_id);
    let pref2 = getPreferredSide(pair.guest_team_id);

    if(circleCount > 1){
      // На каждом новом круге меняем предпочтения для всех команд
      const matchInLastCircle = allSeasonMatches.findLast(m => m.home_team_id === pair.home_team_id && m.guest_team_id === pair.guest_team_id || m.home_team_id === pair.guest_team_id && m.guest_team_id === pair.home_team_id);
      if(matchInLastCircle && matchInLastCircle.id){
        pref1 = matchInLastCircle.home_team_id === pair.home_team_id ? 'away' : 'home';
        pref2 = matchInLastCircle.guest_team_id === pair.guest_team_id ? 'home' : 'away';
      }
    }

    let hId = pair.home_team_id;
    let gId = pair.guest_team_id;
    let hName = pair.home_team_name;
    let gName = pair.guest_team_name;
    let hImg = pair.home_team_image || '';
    let gImg = pair.guest_team_image || '';

    if ((pref1 === 'away' && pref2 !== 'away') || (pref2 === 'home' && pref1 !== 'home')) {
      [hId, gId] = [gId, hId];
      [hName, gName] = [gName, hName];
      [hImg, gImg] = [gImg, hImg];
    }

    return {
      ...pair,
      home_team_id: hId,
      guest_team_id: gId,
      home_team_name: hName,
      guest_team_name: gName,
      home_team_image: hImg,
      guest_team_image: gImg,
    };
  }, [getPreferredSide]);

  const getTeamName = (teamId) => {
    const t = teams.find(t => t.team_id === teamId || t.id === teamId);
    return t ? t.name : `Команда ${teamId}`;
  };

  const getTeamImage = (teamId) => {
    const t = teams.find(t => t.team_id === teamId || t.id === teamId);
    return t ? (t.image || '') : '';
  };

  /**
   * Основной алгоритм генерации матчей
   */
  const generateMatches = async () => {
    if (!league || !season || !stage) {
      setMessage({ text: 'Выберите лигу, сезон и стадию', type: 'error' });
      return;
    }
    if (!selectedStadium) {
      setMessage({ text: 'Выберите стадион', type: 'error' });
      return;
    }
    if (!selectedJudge) {
      setMessage({ text: 'Выберите судью', type: 'error' });
      return;
    }
    if (!halfTime || parseInt(halfTime) <= 0) {
      setMessage({ text: 'Укажите время тайма', type: 'error' });
      return;
    }

    setGenerating(true);
    setMessage({ text: '', type: '' });

    try {
      const headers = await getAuthHeaders();
      // Перезагружаем существующие матчи для актуальности
      const [existingResp, allMatchesResp] = await Promise.all([
        axios.post(
          BASE_URL.url + `/leagues/${league.value}/matches`,
          { season_id: season.value, stage_id: stage.id },
          { headers }
        ),
        axios.post(
          BASE_URL.url + `/leagues/${league.value}/matches`,
          { season_id: season.value },
          { headers }
        )
      ]);
      const existingData = Array.isArray(existingResp.data) ? existingResp.data : (existingResp.data.data || []);
      const currentExisting = existingData.filter(m =>
        m.stage_id === stage.id || m.tour_id === stage.id
      );
      setExistingMatches(currentExisting);

      const allMatchesData = Array.isArray(allMatchesResp.data) ? allMatchesResp.data : (allMatchesResp.data.data || []);
      setAllSeasonMatches(allMatchesData);

      // Загружаем команды если ещё не загружены
      let currentTeams = teams;
      if (currentTeams.length === 0) {
        try {
          const teamsResp = await axios.post(
            BASE_URL.url + `/leagues/${league.value}/teams`,
            { season_id: season.value, ...champFields(champId) },
            { headers }
          );
          const teamsData = Array.isArray(teamsResp.data) ? teamsResp.data : (teamsResp.data.data || []);
          setTeams(teamsData);
          currentTeams = teamsData;
        } catch (err) {
          console.error('Error loading teams:', err);
        }
      }
      // === ГЕНЕРАЦИЯ ===
      let teamsList = [...currentTeams];
      const leagueType = parseInt(league.league_type);

      // Для лиг с группами: загружаем данные о группах
      let currentGroupMap = { ...teamGroupMap };
      let currentGroups = [...groups];

      if (leagueType === 2) {
        if (Object.keys(currentGroupMap).length === 0) {
          const map = {};

          // Используем v2/tournamentTable — возвращает table_data с group_name у каждой команды
          try {
            const tableResp = await axios.post(
              BASE_URL.url + `/leagues/${league.value}/v2/tournamentTable`,
              { season_id: parseInt(season.value) },
              { headers, timeout: 10000 }
            );
            const tableData = tableResp.data?.table_data || tableResp.data;
            if (Array.isArray(tableData)) {
              tableData.forEach(team => {
                const tId = team.team_id || team.id;
                const gName = team.group_name;
                if (tId && gName) {
                  map[tId] = gName;
                }
              });
            }
          } catch (err) {
            // v2/tournamentTable недоступен
          }

          currentGroupMap = map;
          setTeamGroupMap(map);
        }
      }

      const hasGroupData = Object.keys(currentGroupMap).length > 0;

      // Для лиг с группами: проверяем что у нас есть данные
      if (leagueType === 2 && !hasGroupData) {
        setMessage({ text: 'Не удалось загрузить данные о группах. Турнирная таблица пуста — необходимо создать хотя бы один тур с матчами.', type: 'error' });
        setGenerating(false);
        return;
      }

      // Для лиг с группами: фильтруем команды по группе стадии
      if (leagueType === 2 && hasGroupData) {
        let stageGroupName = null;

        if (stage.group_name) {
          stageGroupName = stage.group_name;
        } else if (stage.group_id) {
          const groupById = currentGroups.find(g => (g.id || g.group_id) == stage.group_id);
          stageGroupName = groupById ? (groupById.name || groupById.group_name) : String(stage.group_id);
        }

        if (stageGroupName) {
          teamsList = teamsList.filter(team => {
            const tGroup = currentGroupMap[team.team_id] || currentGroupMap[parseInt(team.team_id)];
            return tGroup === stageGroupName;
          });
        }
      }

      const totalTeams = teamsList.length;
      const matchesPerTour = Math.floor(totalTeams / 2);

      // Существующие пары — только из текущего незавершённого круга.
      // После завершения круга пары снова становятся доступными.
      const existingPairs = new Set();
      const matchesInFullCircle = totalTeams % 2 === 0
        ? totalTeams * (totalTeams - 1) / 2
        : (totalTeams + 1) * totalTeams / 2;

      const sortedAllMatches = [...allMatchesData].sort((a, b) => {
        const stageA = Number(a.stage_id || a.tour_id || 0);
        const stageB = Number(b.stage_id || b.tour_id || 0);
        if (stageA !== stageB) return stageA - stageB;

        const idA = Number(a.id ?? a.match_id ?? 0);
        const idB = Number(b.id ?? b.match_id ?? 0);
        return idA - idB;
      });

      const matchesInCurrentCircle = sortedAllMatches.length % matchesInFullCircle;
      const nextTourIndex = matchesPerTour > 0
        ? Math.floor(matchesInCurrentCircle / matchesPerTour)
        : 0;

      const currentCircleStartIndex = sortedAllMatches.length - matchesInCurrentCircle;
      if (currentCircleStartIndex >= 0) {
        sortedAllMatches.slice(currentCircleStartIndex).forEach(m => {
          existingPairs.add(`${m.home_team_id}-${m.guest_team_id}`);
          existingPairs.add(`${m.guest_team_id}-${m.home_team_id}`);
        });
      }
      // Команды, которые уже задействованы в этом туре
      const busyTeams = new Set();
      currentExisting.forEach(m => {
        busyTeams.add(m.home_team_id);
        busyTeams.add(m.guest_team_id);
      });

      const matchesNeeded = matchesPerTour - currentExisting.length;
      if (matchesNeeded <= 0) {
        setMessage({ text: 'Тур уже заполнен. Все матчи созданы.', type: 'info' });
        setGenerating(false);
        return;
      }

      // === ГЕНЕРАЦИЯ ПАР ===

      const scorePair = (pair) => {
        const pref1 = getPreferredSide(pair.home_team_id);
        const pref2 = getPreferredSide(pair.guest_team_id);
        let score = 0;
        if (pref1 === 'home') score += 2;
        if (pref2 === 'away') score += 2;
        if (pref1 === 'away') score -= 1;
        if (pref2 === 'home') score -= 1;
        return score;
      };
      /**
       * Полное паросочетание через backtracking.
       * Гарантирует, что ВСЕ команды получат пару (если это возможно).
       * Для 6 команд (15 пар) — мгновенно.
       */
      const findPerfectMatching = (pairs, neededCount, busySet) => {
        const result = [];
        const usedInMatch = new Set(busySet);

        const backtrack = (startIdx) => {
          if (result.length === neededCount) return true;

          for (let i = startIdx; i < pairs.length; i++) {
            const pair = pairs[i];
            if (usedInMatch.has(pair.home_team_id) || usedInMatch.has(pair.guest_team_id)) continue;

            result.push(pair);
            usedInMatch.add(pair.home_team_id);
            usedInMatch.add(pair.guest_team_id);

            if (backtrack(i + 1)) return true;

            result.pop();
            usedInMatch.delete(pair.home_team_id);
            usedInMatch.delete(pair.guest_team_id);
          }
          return false;
        };

        backtrack(0);
        return result;
      };

      const selected = [];
      const used = new Set(busyTeams);

      if (leagueType === 2 && hasGroupData) {
        // Группируем команды по группам
        const teamsByGroup = {};
        teamsList.forEach(team => {
          const gName = currentGroupMap[team.team_id] || currentGroupMap[parseInt(team.team_id)];
          if (gName) {
            if (!teamsByGroup[gName]) teamsByGroup[gName] = [];
            teamsByGroup[gName].push(team);
          }
        });
        // Генерируем матчи для каждой группы отдельно
        Object.keys(teamsByGroup).sort().forEach(groupName => {
          const groupTeams = teamsByGroup[groupName];
          const groupMatchesPerTour = Math.floor(groupTeams.length / 2);

          // Уже существующие матчи для этой группы в этом туре
          const groupExistingCount = currentExisting.filter(m => {
            const hGroup = currentGroupMap[m.home_team_id] || currentGroupMap[parseInt(m.home_team_id)];
            return hGroup === groupName;
          }).length;

          const groupMatchesNeeded = groupMatchesPerTour - groupExistingCount;
          if (groupMatchesNeeded <= 0) {
            return;
          }

          // Все возможные пары внутри группы (исключая уже сыгранные за сезон)
          const groupPairs = [];
          for (let i = 0; i < groupTeams.length; i++) {
            for (let j = i + 1; j < groupTeams.length; j++) {
              const p1 = `${groupTeams[i].team_id}-${groupTeams[j].team_id}`;
              const p2 = `${groupTeams[j].team_id}-${groupTeams[i].team_id}`;
              if (!existingPairs.has(p1) && !existingPairs.has(p2)) {
                groupPairs.push({
                  home_team_id: groupTeams[i].team_id,
                  guest_team_id: groupTeams[j].team_id,
                  home_team_name: groupTeams[i].name,
                  guest_team_name: groupTeams[j].name,
                  home_team_image: groupTeams[i].image || '',
                  guest_team_image: groupTeams[j].image || ''
                });
              }
            }
          }

          // Сортируем по предпочтению дом/гости (для лучшего чередования)
          groupPairs.sort((a, b) => scorePair(b) - scorePair(a));

          // Полное паросочетание — гарантирует что все команды получат пару
          const groupBusy = new Set();
          currentExisting.forEach(m => {
            const hGroup = currentGroupMap[m.home_team_id] || currentGroupMap[parseInt(m.home_team_id)];
            if (hGroup === groupName) {
              groupBusy.add(m.home_team_id);
              groupBusy.add(m.guest_team_id);
            }
          });

          const matching = findPerfectMatching(groupPairs, groupMatchesNeeded, groupBusy);

          matching.forEach(pair => {
            const adjusted = applyHomeAway(pair);
            selected.push({
              ...adjusted,
              home_team_name: adjusted.home_team_name || getTeamName(adjusted.home_team_id),
              guest_team_name: adjusted.guest_team_name || getTeamName(adjusted.guest_team_id),
              group_name: groupName
            });
            used.add(adjusted.home_team_id);
            used.add(adjusted.guest_team_id);
          });

        });

      } else {
        // Обычная лига (без групп) — генерируем все возможные пары
        const allPairs = [];
        for (let i = 0; i < teamsList.length; i++) {
          for (let j = i + 1; j < teamsList.length; j++) {
            allPairs.push({
              home_team_id: teamsList[i].team_id,
              guest_team_id: teamsList[j].team_id,
              home_team_name: teamsList[i].name,
              guest_team_name: teamsList[j].name,
              home_team_image: teamsList[i].image || '',
              guest_team_image: teamsList[j].image || ''
            });
          }
        }

        const bergerPairs = createBergerPairs(
          teamsList,
          existingPairs,
          previousStage ? allSeasonMatches.filter(m =>
            m.stage_id === previousStage.id || m.tour_id === previousStage.id
          ) : null
        );

        // Сохраняем все доступные туры
        setAvailableTours(bergerPairs);
        const defaultTourIndex = previousStage
          ? 0
          : Math.min(nextTourIndex, bergerPairs.length - 1);
        setCurrentTourIndex(defaultTourIndex);

        const finalPair = bergerPairs?.[defaultTourIndex]?.matches || [];

        finalPair
          .filter((pair) => pair.home_team_id != null && pair.guest_team_id != null)
          .forEach(pair => {
            const adjusted = applyHomeAway(pair);
            selected.push({
              ...adjusted,
              home_team_name: adjusted.home_team_name || getTeamName(adjusted.home_team_id),
              guest_team_name: adjusted.guest_team_name || getTeamName(adjusted.guest_team_id),
            });
            used.add(adjusted.home_team_id);
            used.add(adjusted.guest_team_id);
          });
      }

      if (selected.length === 0) {
        setMessage({ text: 'Не удалось сгенерировать матчи. Возможно, все пары уже сыграны.', type: 'error' });
        setGenerating(false);
        return;
      }

      // Добавляем настройки
      const stadiumData = stadiums.find(s => s.value === selectedStadium);
      const judgeData = judges.find(j => j.value === selectedJudge);

      const matchesWithSettings = selected.map((m, i) => ({
        ...m,
        date: matchDate || null,
        time: matchTime || null,
        stadium_id: Number(selectedStadium) || 14,
        stadium_name: stadiumData?.name || '',
        judje_id: selectedJudge,
        judje_name: judgeData?.name || '',
        half_time: parseInt(halfTime) || 30,
        extra_time_available: league.cache_extra_time_available || false,
        extra_half_time: league.cache_extra_half_time || 15,
        penalty_kicks_available: league.cache_penalty_kicks_available || false,
        timeline_enabled: !league.cache_timeline_disabled,
        available: true,
        league_id: parseInt(league.value),
        league_name: league.name,
        season_id: parseInt(season.value),
        id: i
      }));

      setGeneratedMatches(matchesWithSettings);
      setDisabledMatches(new Set());
      setStep('preview');
      setMessage({ text: '', type: '' });
    } catch (error) {
      console.error('Error generating matches:', error);
      setMessage({
        text: error.response?.data?.message || 'Ошибка при генерации матчей',
        type: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };

  const createBergerPairs = (teams, playedPairs = new Set(), lastRoundMatches = null) => {
    /**
     * Алгоритм таблицы Бергера для круговой системы
     * Гарантирует: каждая команда играет с каждой, минимум туров, в каждом туре каждая команда играет максимум один раз
     * Возвращает: массив туров, каждый тур содержит массив матчей
     * Если передан lastRoundMatches, сортирует туры так, чтобы выше были те туры, где меньше совпадений по стороне игры с последним туром
     */
    if (!teams || teams.length < 2) return [];
    
    const normalizeTeamId = (team) => {
      if (team == null) return null;
      // Примитив (id)
      if (typeof team !== 'object') return team;
      // Объект команды — вернём корректный id или null
      const id = team.team_id ?? team.id;
      return id != null ? id : null;
    };
    const isValidMatch = (match) => match && match.home_team_id != null && match.guest_team_id != null;
    const buildLastSideMap = (matches) => {
      const sides = {};
      if (!Array.isArray(matches)) return sides;
      matches.forEach((match) => {
        if (!match) return;
        let homeId;
        let guestId;

        if (typeof match === 'string') {
          const [left, right] = match.split('-').map(str => str.trim());
          if (left && right) {
            homeId = left;
            guestId = right;
          }
        } else {
          homeId = normalizeTeamId(match.home_team_id ?? match.homeId ?? match.team1);
          guestId = normalizeTeamId(match.guest_team_id ?? match.guestId ?? match.team2);
        }

        if (homeId !== undefined && homeId !== null) {
          sides[homeId] = 'home';
        }
        if (guestId !== undefined && guestId !== null) {
          sides[guestId] = 'away';
        }
      });
      return sides;
    };

    const scoreTour = (tour, lastSides) => {
      if (!tour || !Array.isArray(tour.matches)) return Number.MAX_SAFE_INTEGER;
      return tour.matches.reduce((total, match) => {
        const aId = normalizeTeamId(match.home_team_id ?? match.home_team?.id ?? match.team1 ?? match.team_id);
        const bId = normalizeTeamId(match.guest_team_id ?? match.guest_team?.id ?? match.team2 ?? match.opponent_id);
        if (aId === undefined || bId === undefined || aId === null || bId === null) return total;

        const aSide = lastSides[aId];
        const bSide = lastSides[bId];
        const optionAhome = (aSide === 'home' ? 1 : 0) + (bSide === 'away' ? 1 : 0);
        const optionBhome = (bSide === 'home' ? 1 : 0) + (aSide === 'away' ? 1 : 0);
        return total + Math.min(optionAhome, optionBhome);
      }, 0);
    };

    let teamList = [...teams];

    const isOdd = teamList.length % 2 === 1;
    
    // Если нечетное количество команд, добавляем фиктивную команду
    if (isOdd) {
      teamList.push({ team_id: null, name: 'BYE', id: null });
    }
    
    const n = teamList.length;
    const tours = [];
    
    // Алгоритм циклического вращения Бергера
    // В каждом туре первая команда остается на месте, остальные вращаются

    for (let round = 0; round < n - 1; round++) {
      const roundMatches = [];
      
      // Для каждого тура создаем матчи
      for (let i = 0; i < n / 2; i++) {
        const homeIdx = i;
        const awayIdx = n - 1 - i;
        
        // Пропускаем матчи с фиктивной командой (BYE)
        const homeId = normalizeTeamId(teamList[homeIdx]);
        const guestId = normalizeTeamId(teamList[awayIdx]);
        if (homeId !== null && guestId !== null) {
          roundMatches.push({
            home_team_id: homeId,
            home_team_name: teamList[homeIdx].name,
            home_team_image: teamList[homeIdx].image || '',
            guest_team_id: guestId,
            guest_team_name: teamList[awayIdx].name,
            guest_team_image: teamList[awayIdx].image || ''
          });
        }
      }
      
      // Добавляем тур только если в нем есть матчи
      if (roundMatches.length > 0) {
        tours.push({ matches: roundMatches });
      }
      
      // Вращаем команды для следующего тура
      // Первая позиция остается, остальные сдвигаются на один место
      if (round < n - 2) {
        const temp = teamList[n - 1];
        for (let i = n - 1; i > 1; i--) {
          teamList[i] = teamList[i - 1];
        }
        teamList[1] = temp;
      }
    }

    // Нормализуем все матчи: гарантируем, что id команд — примитивы (числа/строки),
    // а также убираем любые пары с BYE или незаполненными id.
    const normalizedTours = tours
      .map((tour) => ({
        ...tour,
        matches: tour.matches
          .map((m) => {
            const homeId = normalizeTeamId(m.home_team_id ?? m.home_team ?? m.homeId ?? m.team1 ?? m.home);
            const guestId = normalizeTeamId(m.guest_team_id ?? m.guest_team ?? m.guestId ?? m.team2 ?? m.opponent);
            return {
              home_team_id: homeId,
              guest_team_id: guestId,
              home_team_name: m.home_team_name || (m.home_team && m.home_team.name) || '',
              guest_team_name: m.guest_team_name || (m.guest_team && m.guest_team.name) || '',
              home_team_image: m.home_team_image || (m.home_team && m.home_team.image) || '',
              guest_team_image: m.guest_team_image || (m.guest_team && m.guest_team.image) || ''
            };
          })
          .filter((mm) => mm.home_team_id != null && mm.guest_team_id != null)
      }))
      .filter((tour) => tour.matches.length > 0);

    let filteredTours = normalizedTours;
    if (playedPairs && playedPairs.size > 0) {
      const hasPlayedMatch = (homeId, guestId) => {
        const keyA = `${homeId}-${guestId}`;
        const keyB = `${guestId}-${homeId}`;
        return playedPairs.has(keyA) || playedPairs.has(keyB);
      };

      const isTourFullyPlayed = (tour) => tour.matches.every(match => 
        hasPlayedMatch(normalizeTeamId(match.home_team_id), normalizeTeamId(match.guest_team_id))
      );

      const remainingTours = normalizedTours.filter(tour => !isTourFullyPlayed(tour));
      if (remainingTours.length > 0) {
        filteredTours = remainingTours;
      }
    }

    if (!lastRoundMatches || !Array.isArray(lastRoundMatches) || lastRoundMatches.length === 0) {
      return filteredTours;
    }

    const lastSides = buildLastSideMap(lastRoundMatches);
    return [...filteredTours].sort((a, b) => scoreTour(a, lastSides) - scoreTour(b, lastSides));
  }

  /** Переключает на следующий тур из таблицы Бергера */
  const switchToNextTour = () => {
    if (availableTours.length <= 1) return;
    
    const nextIndex = (currentTourIndex + 1) % availableTours.length;
    setCurrentTourIndex(nextIndex);
    
    // Обновляем сгенерированные матчи для нового тура
    const currentTour = availableTours[nextIndex];
    if (currentTour && currentTour.matches) {
      const selected = [];

      currentTour.matches
        .filter((pair) => pair.home_team_id != null && pair.guest_team_id != null)
        .forEach(pair => {
          const adjusted = applyHomeAway(pair);
          selected.push({
            ...adjusted,
            home_team_name: adjusted.home_team_name || getTeamName(adjusted.home_team_id),
            guest_team_name: adjusted.guest_team_name || getTeamName(adjusted.guest_team_id),
          });
        });
      
      // Добавляем настройки
      const stadiumData = stadiums.find(s => s.value === selectedStadium);
      const judgeData = judges.find(j => j.value === selectedJudge);
      
      const matchesWithSettings = selected.map((m, i) => ({
        ...m,
        date: matchDate || null,
        time: matchTime || null,
        stadium_id: Number(selectedStadium) || 14,
        stadium_name: stadiumData?.name || '',
        judje_id: selectedJudge,
        judje_name: judgeData?.name || '',
        half_time: parseInt(halfTime) || 30,
        extra_time_available: league.cache_extra_time_available || false,
        extra_half_time: league.cache_extra_half_time || 15,
        penalty_kicks_available: league.cache_penalty_kicks_available || false,
        timeline_enabled: !league.cache_timeline_disabled,
        available: true,
        league_id: parseInt(league.value),
        league_name: league.name,
        season_id: parseInt(season.value),
        id: i
      }));
      
      setGeneratedMatches(matchesWithSettings);
      setDisabledMatches(new Set());
    }
  };

  /** Подтверждение и сохранение */
  const confirmAndSave = async () => {
    const toSave = generatedMatches.filter((m, i) => !disabledMatches.has(i) && m.home_team_id != null && m.guest_team_id != null);
    if (toSave.length === 0) {
      setMessage({ text: 'Нет матчей для сохранения. Включите хотя бы один матч.', type: 'error' });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const headers = await getAuthHeaders();
      const isPlayoff = (stage?.isPlayoff === true || Number(stage?.isPlayoff) === 1) ? 1 : 0;
      await axios.post(
        BASE_URL.url + `/leagues/changeStage/${stage.id}`,
        {
          name: stage.name,
          short_name: stage.short_name,
          priority: stage.priority,
          group_id: stage.group_id,
          isPlayoff,
          league_id: parseInt(league.value),
          season_id: parseInt(season.value),
          matches: toSave.map((match) => {
            const stadiumObj = stadiums.find((item) => (
              Number(item.value) === Number(match.stadium_id)
              || Number(item.id) === Number(match.stadium_id)
            )) || null;
            const matchTimezone = resolveMatchTimezone(stadiumObj, league, championship);
            const kickoff = kickoffFieldsForApi(match.date, match.time, matchTimezone);
            return {
              ...match,
              isPlayoff,
              date: kickoff.date,
              time: kickoff.time,
              timezone: matchTimezone,
            };
          }),
        },
        { headers }
      );

      setMessage({ text: `Успешно создано матчей: ${toSave.length}`, type: 'success' });
      setTimeout(() => {
        resetForm();
        if (onSuccess) onSuccess();
        onClose();
      }, 400);
    } catch (error) {
      console.error('Error saving matches:', error);
      setMessage({
        text: error.response?.data?.message || 'Ошибка при сохранении матчей',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setStep('settings');
    setMatchDate('');
    setMatchTime('');
    setSelectedStadium('');
    setSelectedJudge('');
    setHalfTime('');
    setGeneratedMatches([]);
    setDisabledMatches(new Set());
    setAvailableTours([]);
    setCurrentTourIndex(0);
    setMessage({ text: '', type: '' });
  };

  const goBackToSettings = () => {
    setStep('settings');
    setGeneratedMatches([]);
    setDisabledMatches(new Set());
    setAvailableTours([]);
    setCurrentTourIndex(0);
    setMessage({ text: '', type: '' });
  };

  const toggleMatchDisabled = (index) => {
    setDisabledMatches((prevDisabled) => {
      const nextDisabled = new Set(prevDisabled);
      if (nextDisabled.has(index)) {
        nextDisabled.delete(index);
      } else {
        nextDisabled.add(index);
      }
      return nextDisabled;
    });
  };

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, { keys: ['name'], threshold: 0.3 });
    return (value) => {
      if (!value.length) return options;
      return fuse.search(value).map(r => r.item);
    };
  };

  if (!isOpen) return null;

  const enabledCount = generatedMatches.length - disabledMatches.size;

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="modal-content create-match-modal"
        style={{ maxWidth: step === 'preview' ? '800px' : '500px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            {step === 'settings' ? 'Сгенерировать матчи' : 'Превью сгенерированных матчей'}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        <div className="modal-body">
          {message.text && (
            <div className={`message-box ${
              message.type === 'success' ? 'message-success' :
              message.type === 'error' ? 'message-error' :
              'message-info'
            }`}>
              {message.text}
            </div>
          )}

          <div className="info-section">
            <p><strong>Лига:</strong> {league?.name}</p>
            <p><strong>Сезон:</strong> {season?.name}</p>
            <p><strong>Стадия:</strong> {stage?.name}</p>
            {parseInt(league?.league_type) === 2 && (
              <p style={{ color: '#9ef0d0', fontSize: '13px' }}>
                <FiAlertCircle style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Групповая лига — матчи генерируются внутри групп
              </p>
            )}
            {existingMatches.length > 0 && (
              <p className="warning-text">
                <strong>Существующие матчи в туре:</strong> {existingMatches.length}
              </p>
            )}
          </div>

          {/* ============ ШАГ 1: НАСТРОЙКИ ============ */}
          {step === 'settings' && (
            <form onSubmit={(e) => { e.preventDefault(); generateMatches(); }}>
              <div className="form-section-modal">
                <label className="form-label-modal">Дата матчей (необязательно)</label>
                <div className="cm-native-dt">
                  <input
                    className="input-field-modal"
                    type="date"
                    value={matchDate}
                    onChange={(e) => setMatchDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-section-modal">
                <label className="form-label-modal">Время матчей (необязательно)</label>
                <div className="cm-native-dt">
                  <input
                    className="input-field-modal"
                    type="time"
                    value={matchTime}
                    onChange={(e) => setMatchTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-section-modal">
                <label className="form-label-modal">Стадион</label>
                <SelectSearch
                  options={stadiums}
                  value={selectedStadium}
                  search
                  filterOptions={fuzzySearch}
                  placeholder="Выберите стадион"
                  emptyMessage="Стадион не найден"
                  onChange={setSelectedStadium}
                />
              </div>

              <div className="form-section-modal">
                <label className="form-label-modal">Судья</label>
                <SelectSearch
                  options={judges}
                  value={selectedJudge}
                  search
                  filterOptions={fuzzySearch}
                  placeholder="Выберите судью"
                  emptyMessage="Судья не найден"
                  onChange={setSelectedJudge}
                />
              </div>

              <div className="form-section-modal">
                <label className="form-label-modal">Время тайма (мин)</label>
                <input
                  className="input-field-modal"
                  type="number"
                  min="1"
                  max="90"
                  value={halfTime}
                  onChange={(e) => setHalfTime(e.target.value)}
                  placeholder="Напр. 30 или 45"
                  required
                />
              </div>

              <div className="button-group-modal">
                <button
                  type="submit"
                  disabled={generating}
                  className="btn-modal btn-primary-modal"
                >
                  {generating ? 'Генерация...' : 'Сгенерировать матчи'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={generating}
                  className="btn-modal btn-secondary-modal"
                >
                  Отмена
                </button>
              </div>
            </form>
          )}

          {/* ============ ШАГ 2: ПРЕВЬЮ ============ */}
          {step === 'preview' && (
            <div className="generate-preview">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <p style={{ fontSize: '14px', color: '#4a5568', margin: 0 }}>
                  Сгенерировано матчей: <strong>{generatedMatches.length}</strong>.
                  Включено: <strong>{enabledCount}</strong>.
                  Снимите галочку, чтобы исключить матч.
                </p>
                {availableTours.length > 1 && (
                  <button
                    onClick={switchToNextTour}
                    className="btn-icon"
                    style={{ 
                      background: 'none', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '6px', 
                      padding: '6px',
                      cursor: 'pointer',
                      color: '#4a5568'
                    }}
                    title={`Вариант ${currentTourIndex + 1} из ${availableTours.length}`}
                  >
                    <FiRefreshCw size={16} />
                  </button>
                )}
              </div>

              <div className="generated-matches-list">
                {generatedMatches.map((match, index) => {
                  const isOff = disabledMatches.has(index);
                  const showGroupHeader = match.group_name && (
                    index === 0 || generatedMatches[index - 1].group_name !== match.group_name
                  );
                  return (
                    <React.Fragment key={index}>
                      {showGroupHeader && (
                        <div className="generate-group-header" style={{ margin: index > 0 ? '10px 0 4px' : '0 0 4px' }}>
                          Группа {match.group_name}
                        </div>
                      )}
                      <div
                        className={`generated-match-row ${isOff ? 'match-disabled' : ''}`}
                      >
                      <label className="match-checkbox-label">
                        <input
                          type="checkbox"
                          checked={!isOff}
                          onChange={() => toggleMatchDisabled(index)}
                        />
                      </label>
                      <div className="match-pair-info">
                        <div className="match-team home-team">
                          {match.home_team_image && (
                            <img
                              src={getTeamImageUrl(match.home_team_image)}
                              alt=""
                              className="team-logo-small"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <span>{match.home_team_name}</span>
                          <span className="side-label home-label">дома</span>
                        </div>
                        <span className="match-vs">vs</span>
                        <div className="match-team away-team">
                          {match.guest_team_image && (
                            <img
                              src={getTeamImageUrl(match.guest_team_image)}
                              alt=""
                              className="team-logo-small"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <span>{match.guest_team_name}</span>
                          <span className="side-label away-label">гости</span>
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Настройки матчей */}
              <div className="preview-settings-info">
                <p><strong>Дата:</strong> {matchDate ? moment(matchDate).format('DD.MM.YYYY') : '—'}</p>
                {matchTime && <p><strong>Время:</strong> {matchTime}</p>}
                <p><strong>Стадион:</strong> {stadiums.find(s => s.value === selectedStadium)?.name || '—'}</p>
                <p><strong>Судья:</strong> {judges.find(j => j.value === selectedJudge)?.name || '—'}</p>
                <p><strong>Время тайма:</strong> {halfTime} мин</p>
              </div>

              <div className="button-group-modal">
                <button
                  type="button"
                  onClick={goBackToSettings}
                  disabled={saving}
                  className="btn-modal btn-secondary-modal"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <FiArrowLeft /> Назад
                </button>
                <button
                  type="button"
                  onClick={confirmAndSave}
                  disabled={saving || enabledCount === 0}
                  className="btn-modal btn-primary-modal"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  {saving ? 'Сохранение...' : (
                    <>
                      <FiCheck /> Подтвердить ({enabledCount})
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GenerateTourModal;
